import {
  Component,
  ElementRef,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { HeaderComponent } from '../header/header';
import { FooterComponent } from '../footer/footer';
import { LeadPopupComponent } from '../../shared/components/lead-popup/lead-popup.component';
import { MetaPixelService } from '../../core/analytics/meta-pixel.service';

interface CtaMessage { hook: string; action: string; }

// ARHIVĂ CTA_MESSAGES — 2026-06-06 — NU ȘTERGE
// { hook: 'Facem totul altfel.',   action: 'Vezi cum →'      },
// { hook: 'Grijă ca acasă.',       action: 'Află mai mult →' },
const CTA_MESSAGES: CtaMessage[] = [
  { hook: 'Copilul tău merită.',  action: 'Scrie-ne →'   },
  { hook: 'Aici cresc fericiți.', action: 'Hai la noi →' },
];

const FADE_MS   = 300;
const HOOK_MS   = 4000;
const ACTION_MS = 2000;

type ScrollTarget = Window | Element;

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [HeaderComponent, RouterOutlet, FooterComponent, LeadPopupComponent],
  templateUrl: './main-layout.html',
  styleUrl: './main-layout.scss',
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  private readonly whatsappUrl = 'https://wa.me/40752555111?utm_source=website&utm_medium=cta&utm_campaign=homepage&utm_content=sticky&text=Bun%C4%83%21%20A%C8%99%20vrea%20s%C4%83%20aflu%20mai%20multe%20despre%20C%C4%83su%C8%9Ba%20Jocului%20%F0%9F%8C%9F';
  @ViewChild('contentRef', { static: true }) contentRef!: ElementRef<HTMLElement>;

  hidden = false;
  scrolled = false;
  fabVisible = false;

  ctaText    = CTA_MESSAGES[0].hook;
  ctaVisible = true;

  private ctaIndex  = 0;
  private ctaTimers: ReturnType<typeof setTimeout>[] = [];

  private target!: ScrollTarget;
  private lastY = 0;
  private ticking = false;
  private boundHandler = (event?: Event) => this.onScroll(event);
  private navSub?: Subscription;

  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly metaPixel = inject(MetaPixelService);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  constructor(private zone: NgZone) {}

  ngOnInit(): void {
    if (!this.isBrowser) return;

    const contentEl = this.contentRef?.nativeElement;
    const style = contentEl ? getComputedStyle(contentEl) : null;
    const contentIsScroller =
      !!contentEl &&
      (style?.overflowY === 'auto' || style?.overflowY === 'scroll');

    this.target = contentIsScroller ? contentEl! : window;

    this.zone.runOutsideAngular(() => {
      this.addScrollListener();
      // setează starea inițială corectă
      this.processScroll(this.getScrollTop());
    });

    this.navSub = this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => this.scrollToTop());

    this.startCtaRotation();
  }

  ngOnDestroy(): void {
    if (!this.isBrowser) return;
    this.removeScrollListener();
    this.navSub?.unsubscribe();
    this.stopCtaRotation();
  }

  onWhatsAppClick(): void {
    this.metaPixel.trackLead({ content_name: 'whatsapp_fab' });
    this.metaPixel.custom('WhatsAppClick', {
      placement: 'floating_button',
      destination: 'whatsapp',
    });
    this.ga4('whatsapp_click', { placement: 'floating_button', campaign: 'homepage' });

    if (!this.isBrowser) return;
    window.open(this.whatsappUrl, '_blank', 'noopener,noreferrer');
  }

  private ga4(event: string, params: Record<string, string | number>): void {
    const dl = (window as { dataLayer?: unknown[] }).dataLayer ?? [];
    (window as { dataLayer?: unknown[] }).dataLayer = dl;
    dl.push({ event, ...params });
  }

  private startCtaRotation(): void {
    if (!this.isBrowser) return;
    this.scheduleCtaStep();
  }

  private scheduleCtaStep(): void {
    const msg = CTA_MESSAGES[this.ctaIndex];

    this.ctaText    = msg.hook;
    this.ctaVisible = true;

    this.ctaTimers.push(setTimeout(() => {
      this.ctaVisible = false;

      this.ctaTimers.push(setTimeout(() => {
        this.ctaText    = msg.action;
        this.ctaVisible = true;

        this.ctaTimers.push(setTimeout(() => {
          this.ctaVisible = false;

          this.ctaTimers.push(setTimeout(() => {
            this.ctaIndex = (this.ctaIndex + 1) % CTA_MESSAGES.length;
            this.scheduleCtaStep();
          }, FADE_MS));
        }, ACTION_MS));
      }, FADE_MS));
    }, HOOK_MS));
  }

  private stopCtaRotation(): void {
    this.ctaTimers.forEach(t => clearTimeout(t));
    this.ctaTimers = [];
  }

  private scrollToTop(): void {
    if (this.target instanceof Window) {
      window.scrollTo({ top: 0, behavior: 'instant' });
      // fallback pentru browsere vechi / body-scroll
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    } else {
      (this.target as Element).scrollTop = 0;
    }
    this.lastY = 0;
  }

  private addScrollListener() {
    if (this.target instanceof Window) {
      window.addEventListener('scroll', this.boundHandler, { passive: true });
    } else {
      (this.target as Element).addEventListener('scroll', this.boundHandler, { passive: true });
    }
    document.addEventListener('scroll', this.boundHandler, { passive: true, capture: true });
  }

  private removeScrollListener() {
    if (this.target instanceof Window) {
      window.removeEventListener('scroll', this.boundHandler);
    } else if (this.target) {
      (this.target as Element).removeEventListener('scroll', this.boundHandler);
    }
    document.removeEventListener('scroll', this.boundHandler, true);
  }

  private getScrollTop(source?: ScrollTarget): number {
    const target = source ?? this.target;

    if (target instanceof Window) {
      return Math.max(
        window.scrollY || 0,
        document.documentElement.scrollTop || 0,
        document.body.scrollTop || 0,
        this.contentRef?.nativeElement?.scrollTop || 0,
      );
    }

    return (target as Element).scrollTop || 0;
  }

  private onScroll(event?: Event) {
    const source = this.resolveScrollSource(event);
    const y = this.getScrollTop(source);
    if (this.ticking) return;
    this.ticking = true;

    requestAnimationFrame(() => {
      this.processScroll(y);
      this.ticking = false;
    });
  }

  private resolveScrollSource(event?: Event): ScrollTarget {
    const eventTarget = event?.target;
    if (eventTarget instanceof Element && eventTarget.scrollHeight > eventTarget.clientHeight) {
      return eventTarget;
    }

    if (eventTarget === document || eventTarget === document.documentElement || eventTarget === document.body) {
      return window;
    }

    return this.target;
  }

  private processScroll(y: number) {
    const directionThreshold = 6;
    const delta = y - this.lastY;
    const goingDown = delta > directionThreshold;
    const goingUp = delta < -directionThreshold;

    const nextScrolled = y > 8;
    const nextFabVisible = y >= 900;
    let nextHidden = this.hidden;

    if (y <= 0) {
      nextHidden = false;
    } else if (goingUp) {
      nextHidden = false;
    } else if (goingDown) {
      nextHidden = true;
    }

    // actualizează în Angular doar la schimbări reale
    if (nextScrolled !== this.scrolled || nextHidden !== this.hidden || nextFabVisible !== this.fabVisible) {
      this.zone.run(() => {
        this.scrolled   = nextScrolled;
        this.hidden     = nextHidden;
        this.fabVisible = nextFabVisible;
      });
    }

    this.lastY = y;
  }
}
